import { useEffect, useRef, useState, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import mqtt from 'mqtt';
import { signIoTUrl } from '../lib/sigv4';

const IOT_ENDPOINT = import.meta.env.VITE_IOT_ENDPOINT ?? '';
const AWS_REGION = import.meta.env.VITE_COGNITO_USER_POOL_ID?.split('_')[0] ?? 'ap-southeast-2';

export type TerminalStatus = 'disconnected' | 'connecting' | 'waiting' | 'active' | 'error';

interface UseTerminalOptions {
  serial: string;
  onData: (data: string) => void;
}

interface UseTerminalResult {
  status: TerminalStatus;
  error: string | null;
  open: (cols: number, rows: number) => void;
  sendInput: (data: string) => void;
  resize: (cols: number, rows: number) => void;
  close: () => void;
}

/**
 * MQTT-based terminal hook.
 *
 * Topic structure:
 *   kinetica/{serial}/terminal/open    — portal → device (start shell)
 *   kinetica/{serial}/terminal/input   — portal → device (keystrokes)
 *   kinetica/{serial}/terminal/output  — device → portal (shell output)
 *   kinetica/{serial}/terminal/resize  — portal → device (terminal size)
 *   kinetica/{serial}/terminal/close   — portal → device (end session)
 *   kinetica/{serial}/terminal/status  — device → portal (session state)
 *
 * Each hook instance carries a sessionId so the device can correlate open/close
 * to a specific PTY. This also prevents clientId collisions when multiple terminals
 * are open simultaneously (different devices or browser tabs from the same identity).
 */
export function useTerminal({ serial, onData }: UseTerminalOptions): UseTerminalResult {
  const [status, setStatus] = useState<TerminalStatus>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const clientRef = useRef<mqtt.MqttClient | null>(null);
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  // Stable per-instance session id — does not change across reconnects so the
  // device can use it to clean up the right PTY even after a network blip.
  const sessionIdRef = useRef(`${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`);

  // Track whether the user explicitly opened a PTY so we can re-open after a
  // transparent MQTT reconnect (device side closes stale sessions on disconnect).
  const openParamsRef = useRef<{ cols: number; rows: number } | null>(null);

  // Caller role from the Cognito ID token (custom:role claim injected by pre-token-gen Lambda).
  // Device requires engineer+ to open a shell; default to 'operator' when claim is absent.
  const callerRoleRef = useRef<string>('operator');

  const prefix = `kinetica/${serial}/terminal`;

  const connect = useCallback(async () => {
    if (!IOT_ENDPOINT || !serial) return;

    setStatus('connecting');
    setError(null);

    try {
      const session = await fetchAuthSession();
      const creds = session.credentials;
      if (!creds) {
        setError('No credentials — identity pool not configured');
        setStatus('error');
        return;
      }

      const identityId = session.identityId;
      if (!identityId) {
        setError('No identity ID');
        setStatus('error');
        return;
      }

      // Extract the caller's role from the Cognito ID token (custom:role claim).
      callerRoleRef.current =
        (session.tokens?.idToken?.payload?.['custom:role'] as string | undefined) ?? 'operator';

      // Include sessionId in clientId so concurrent terminals from the same
      // identity don't collide and kick each other off IoT Core.
      const clientId = `operator-${identityId}-terminal-${sessionIdRef.current}`;
      const url = await signIoTUrl(IOT_ENDPOINT, AWS_REGION, {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        sessionToken: creds.sessionToken,
      });

      const client = mqtt.connect(url, {
        clientId,
        protocolVersion: 4,
        reconnectPeriod: 5000,
        connectTimeout: 10_000,
        // NOTE: the URL is captured at connect-time and reused on every
        // reconnect. STS temporary credentials expire (~1h), so a reconnect
        // after expiry will fail at the IoT Core WebSocket handshake.
        // Re-calling signIoTUrl on each reconnect is tracked as a follow-up.
        transformWsUrl: () => url,
      });

      clientRef.current = client;

      client.on('connect', () => {
        console.log('[Terminal] MQTT connected, sessionId:', sessionIdRef.current);
        client.subscribe([`${prefix}/output`, `${prefix}/status`], (err) => {
          if (err) {
            console.error('[Terminal] Subscribe failed:', err);
            return;
          }
          // If we had an active PTY before this reconnect, re-open it so the
          // device can start a fresh shell (device closes orphaned PTYs on
          // client disconnect, so we need to re-request one).
          if (openParamsRef.current) {
            const { cols, rows } = openParamsRef.current;
            client.publish(
              `${prefix}/open`,
              JSON.stringify({
                sessionId: sessionIdRef.current,
                cols,
                rows,
                shell: '/bin/bash',
                term: 'xterm-256color',
                callerRole: callerRoleRef.current,
              }),
            );
          }
          setStatus('waiting');
        });
      });

      client.on('reconnect', () => {
        console.log('[Terminal] MQTT reconnecting…');
        setStatus('connecting');
      });

      client.on('message', (_topic, payload) => {
        const topic = _topic;
        if (topic === `${prefix}/output`) {
          // Output is base64-encoded terminal data
          try {
            const msg = JSON.parse(payload.toString());
            // Filter output to our session only — ignore stale PTY output from
            // a prior session if the device sends it before cleaning up.
            if (msg.sessionId && msg.sessionId !== sessionIdRef.current) return;
            const decoded = atob(msg.data);
            onDataRef.current(decoded);
          } catch {
            onDataRef.current(payload.toString());
          }
        } else if (topic === `${prefix}/status`) {
          try {
            const msg = JSON.parse(payload.toString());
            // Ignore status messages that belong to a different session.
            if (msg.sessionId && msg.sessionId !== sessionIdRef.current) return;
            if (msg.state === 'active') setStatus('active');
            else if (msg.state === 'closed') {
              openParamsRef.current = null;
              setStatus('disconnected');
            } else if (msg.state === 'error') {
              // Clear openParamsRef so we don't auto-reopen on the next MQTT
              // reconnect — the device PTY is torn down on error and re-opening
              // immediately would likely fail again.
              openParamsRef.current = null;
              setError(msg.message || 'Device terminal error');
              setStatus('error');
            }
          } catch {
            // Ignore malformed status
          }
        }
      });

      client.on('error', (err) => {
        console.error('[Terminal] MQTT error:', err);
        setError(err.message);
        setStatus('error');
      });

      client.on('close', () => {
        // Only move to disconnected if we're not already reconnecting — the
        // 'reconnect' event fires before 'close' but just in case.
        setStatus((prev) => (prev === 'connecting' ? prev : 'disconnected'));
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed');
      setStatus('error');
    }
  }, [serial, prefix]);

  // Connect on mount
  useEffect(() => {
    connect();
    return () => {
      if (clientRef.current) {
        // Best-effort close — device cleans up on MQTT disconnect too, but an
        // explicit close lets it release the PTY immediately.
        clientRef.current.publish(
          `${prefix}/close`,
          JSON.stringify({ sessionId: sessionIdRef.current, reason: 'user' }),
        );
        clientRef.current.end(true);
        clientRef.current = null;
      }
    };
  }, [connect, prefix]);

  const open = useCallback(
    (cols: number, rows: number) => {
      if (!clientRef.current) return;
      openParamsRef.current = { cols, rows };
      clientRef.current.publish(
        `${prefix}/open`,
        JSON.stringify({
          sessionId: sessionIdRef.current,
          cols,
          rows,
          shell: '/bin/bash',
          term: 'xterm-256color',
          callerRole: callerRoleRef.current,
        }),
      );
      setStatus('waiting');
    },
    [prefix],
  );

  const sendInput = useCallback(
    (data: string) => {
      if (!clientRef.current) return;
      clientRef.current.publish(
        `${prefix}/input`,
        JSON.stringify({ sessionId: sessionIdRef.current, data: btoa(data) }),
      );
    },
    [prefix],
  );

  const resize = useCallback(
    (cols: number, rows: number) => {
      if (!clientRef.current) return;
      clientRef.current.publish(
        `${prefix}/resize`,
        JSON.stringify({ sessionId: sessionIdRef.current, cols, rows }),
      );
    },
    [prefix],
  );

  const close = useCallback(() => {
    if (!clientRef.current) return;
    openParamsRef.current = null;
    clientRef.current.publish(
      `${prefix}/close`,
      JSON.stringify({ sessionId: sessionIdRef.current, reason: 'user' }),
    );
    setStatus('disconnected');
  }, [prefix]);

  return { status, error, open, sendInput, resize, close };
}
